/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {AdminBrandfamDialogGetRequest} from '../models/admin-brandfam-dialog-get-request.model';
import {GetBrandFamResultList} from '../models/admin-brandfam-result-list.model';
import {AdminBrandfamTableGetRequest} from '../models/admin-brandfam-table-get-request.model';
import {AdminBrandfamUpdateRequest} from '../models/admin-brandfam-update-request.model';
import {SegmentReport} from '../models/reports/segment-report.model';
import {ConfigService} from './config.service';

const PLANNING_MARKET = 'planning-market';
const MARKET_DIVISION = 'market-division';
const LOCAL_BRAND = 'local-brand';
const PROGRAM_CODE = 'program-code';
const STYLE_CODE = 'style-code';
const BODY_STYLE = 'body-style';
const BRAND_FAM = 'brandfam';
const REGION = 'region';
const SUBREGION = 'subregion';
const GLOBAL_SEGMENT = 'global-segment';
const PROP_SEGMENT = 'propulsion-segment';
const BRAND = 'brand';
const USER = 'user';

@Injectable({
  providedIn: 'root'
})

export class ManageBrandFamilyService {
  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * returns the data to fill the dropdowns
   * @param  req the request object that houses all the parameters
   * @returns a list of strings that populate the dropdowns
   */
  getData(req: AdminBrandfamDialogGetRequest): Observable<string[]> {
    const params = new HttpParams().set(USER, req.user).set(REGION, req.selectedRegion)
    .set(SUBREGION, req.selectedSubregion).set(PLANNING_MARKET, req.selectedPlanningMarket)
    .set(MARKET_DIVISION, req.selectedMarketDivision).set(BRAND, req.selectedBrand).set(LOCAL_BRAND, req.selectedLocalBrand)
    .set(PROGRAM_CODE, req.selectedProgramCode).set(STYLE_CODE, req.selectedStyleCode).set(BODY_STYLE, req.selectedBodyStyle)
    .set(BRAND_FAM, req.selectedBrandFam).set(GLOBAL_SEGMENT, req.selectedGSeg).set(PROP_SEGMENT, req.selectedPropSeg);
    return this.http.get<string[]>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/${req.path}`, {params}).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * returns the data to populate the main data table
   * @param  req the request object that houses all the parameters
   * @returns a list of strings that populate the dropdowns
   */
  getTableData(req: AdminBrandfamTableGetRequest): Observable<GetBrandFamResultList[]> {
    const params = new HttpParams().set(USER, req.user).set(REGION, req.selectedRegion)
    .set(SUBREGION, req.selectedSubregion).set(PLANNING_MARKET, req.selectedPlanningMarket)
    .set(MARKET_DIVISION, req.selectedMarketDivision).set(BRAND, req.selectedBrand).set(LOCAL_BRAND, req.selectedLocalBrand)
    .set(PROGRAM_CODE, req.selectedProgramCode).set(STYLE_CODE, req.selectedStyleCode).set(BODY_STYLE, req.selectedBodyStyle)
    .set(BRAND_FAM, req.selectedBrandFam).set(GLOBAL_SEGMENT, req.selectedGSeg).set(PROP_SEGMENT, req.selectedPropSeg)
    .set('show-plngmkt', req.showPlngmkt);
    return this.http.get<GetBrandFamResultList[]>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/results`,
      {params}).pipe(map((res) => res), catchError(myError => this.handleError(myError)));
  }

  /**
   * returns the data to populate the dropdown for editing brandfam/global segment/ev-ice segment
   * @param user the GMID of the user making the request
   * @param parameter the parameter to build the request
   * @param path the path for the request endpoint
   * @returns a list of strings that populate the dropdowns
   */
  getUpdateDropdownData(user: string, parameter: string, path: string): Observable<string[]> {
    let params = new HttpParams().set(USER, user);
    if (path === 'brandfams') {
      params = new HttpParams().set(USER, user).set(REGION, parameter);
    } else if (path === 'propulsion-segments') {
      params = new HttpParams().set(USER, user).set(GLOBAL_SEGMENT, parameter);
    }
    return this.http.get<string[]>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/results/${path}`, {params}).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * updates the filtered list with the new value provided
   * updates brandfam, global segment, or ev/ice segment based on request path
   *
   * @param  req the request object that houses all the parameters, including the path that it is going to
   */
  updateTheList(req: AdminBrandfamUpdateRequest): Observable<any> {
    const params = new HttpParams().set(USER, req.user).set(REGION, req.selectedRegion)
    .set(SUBREGION, req.selectedSubregion).set(PLANNING_MARKET, req.selectedPlanningMarket)
    .set(MARKET_DIVISION, req.selectedMarketDivision).set(BRAND, req.selectedBrand).set(LOCAL_BRAND, req.selectedLocalBrand)
    .set(PROGRAM_CODE, req.selectedProgramCode).set(STYLE_CODE, req.selectedStyleCode).set(BODY_STYLE, req.selectedBodyStyle)
    .set(BRAND_FAM, req.selectedBrandFam).set(GLOBAL_SEGMENT, req.selectedGSeg).set(PROP_SEGMENT, req.selectedPropSeg)
    .set('new-value', req.newValue);
    return this.http.put<any>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/results/${req.path}`, null, {params}).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * gets the data to populate the segment report table
   * @returns a List of all Segment Reports
   */
  getSegmentReport(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/segment-report`).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * Updates a list of brandfam's local segment with the one provided
   * @param brandfamList - a list of changed ev ice segments to be updated
   */
  updateSegment(brandfamList: SegmentReport[]): Observable<any> {
    return this.http.put<any>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/segment-report`, brandfamList).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * Retrieves the valid EV/ICE segments to update the brandfam report with
   * @returns a list of EV/ICE segments
   */
  getValidSegments(): Observable<string[]> {
    return this.http.get<string[]>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/segment-report/validate`).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * Transforms the segment report data the user uploads to an object to represent the data
   * @param file the segment report data file that is uploaded
   * @param sheetList a list containing the selected sheets to read data from
   */
  transform(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/segment-report/transform`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(error => this.handleError(error)));
  }


  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
